import { motion } from 'framer-motion';
import { DollarSign, Target, Zap, TrendingDown, Map, Clock } from 'lucide-react';
import { fadeInUp, staggerContainer, viewportConfig } from '@/lib/animations';

const values = [
  {
    icon: DollarSign,
    decorIcon: TrendingDown,
    title: "Affordable Entry Point",
    description: "Access broadcast-quality advertising without enterprise budgets. Flexible minimums for any organization size.",
    accent: "orange",
  },
  {
    icon: Target,
    decorIcon: Map,
    title: "Pinpoint Precision",
    description: "Target by ZIP code, city, DMA, or draw custom geo-fences. Reach only the audiences that matter.",
    accent: "teal",
  },
  {
    icon: Zap,
    decorIcon: Clock,
    title: "Days, Not Months",
    description: "From first call to live campaign in under two weeks. Our streamlined process eliminates delays.",
    accent: "orange",
  },
];

const stats = [
  { value: '7+', label: 'Years Experience' },
  { value: 'SDVOSB', label: 'Certified' },
  { value: 'MBO', label: 'Certified' },
  { value: '50', label: 'States Covered' },
];

export function ValueProposition() {
  return (
    <section className="py-12 sm:py-14 md:py-16 lg:py-20 bg-white">
      <motion.div
        initial="hidden"
        whileInView="visible"
        viewport={viewportConfig}
        variants={staggerContainer}
        className="container mx-auto px-5 sm:px-6"
      >
        {/* Section Header */}
        <motion.div variants={fadeInUp} className="text-center mb-12 md:mb-16">
          <span className="text-teal font-semibold text-xs sm:text-sm tracking-widest uppercase mb-2 sm:mb-3 block">
            The Yaw Advantage
          </span>
          <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-navy-deep tracking-tight">
            Premium Inventory. Precise Targeting.{' '}
            <span className="text-orange">Powerful Results.</span>
          </h2>
        </motion.div>

        {/* Three Column Value Props */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-14">
          {values.map((value, index) => (
            <motion.div
              key={value.title}
              variants={fadeInUp}
              custom={index}
              className="text-center group"
            >
              {/* Animated Icon Container */}
              <div className="relative inline-flex items-center justify-center mb-6">
                <motion.div
                  animate={{ scale: [1, 1.1, 1] }}
                  transition={{ duration: 3, repeat: Infinity, delay: index * 0.3 }}
                  className={`w-20 h-20 rounded-2xl ${
                    value.accent === 'orange' ? 'bg-orange/10' : 'bg-teal/10'
                  } flex items-center justify-center`}
                >
                  <value.icon className={`w-9 h-9 ${
                    value.accent === 'orange' ? 'text-orange' : 'text-teal'
                  }`} />
                </motion.div>
                {/* Floating decorator */}
                <motion.div
                  animate={{ y: [-3, 3, -3], rotate: [0, 10, 0] }}
                  transition={{ duration: 2.5, repeat: Infinity }}
                  className={`absolute -top-2 -right-2 p-1.5 rounded-lg ${
                    value.accent === 'orange' ? 'bg-orange' : 'bg-teal'
                  }`}
                >
                  <value.decorIcon className="w-3.5 h-3.5 text-white" />
                </motion.div>
              </div>

              <h3 className="text-xl font-bold text-navy-deep mb-3">
                {value.title}
              </h3>
              <p className="text-muted-foreground leading-relaxed">
                {value.description}
              </p>
            </motion.div>
          ))}
        </div>

        {/* Stats Row */}
        <motion.div
          variants={fadeInUp}
          className="bg-gray-50 rounded-2xl p-6 sm:p-8"
        >
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-6 sm:gap-8">
            {stats.map((stat, index) => (
              <motion.div
                key={stat.label}
                initial={{ opacity: 0, scale: 0.9 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ delay: 0.1 * index, duration: 0.4 }}
                className="text-center"
              >
                <div className="text-2xl sm:text-3xl md:text-4xl font-bold text-orange mb-1">
                  {stat.value}
                </div>
                <div className="text-muted-foreground text-sm font-medium">
                  {stat.label}
                </div>
              </motion.div>
            ))}
          </div>
        </motion.div>
      </motion.div>
    </section>
  );
}
